import React, { useState, useEffect, useMemo } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// sweetalert2 components
import Swal from "sweetalert2";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftSelect from "components/SoftSelect";

import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";

// Soft UI Dashboard PRO React contexts
import { useSoftUIController } from "context";
import { Checkbox, FormControl, FormControlLabel, FormGroup, Grid, Icon } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftDatePicker from "components/SoftDatePicker";

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";
import SoftInput from "components/SoftInput";

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    minWidth: "500px",
    maxWidth: "1000px !important",
    width: "100%",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

function CreateSignal({ showModal, signalCloseModal }) {
  const [controller] = useSoftUIController();
  const { providers = [], sports = [] } = controller || {};

  const [open, setOpen] = useState(false);
  const [signalTypes, setSignalTypes] = useState([
    { value: 0, label: "Provider-Down" },
    { value: 1, label: "Provider-Up" },
    { value: 2, label: "Scheduled-Maintenance" },
    // { value: 3, label: "Coverage Lost - Stuck Events" },
    { value: 4, label: "Block Events" },
    { value: 5, label: "UnBlock Events" },
  ]);

  const [bodyCategories, setBodyCategories] = useState([
    { value: 1, label: "general" },
    { value: 0, label: "specific-inform" },
  ]);

  const [signalTypeSelected, setSignalTypeSelected] = useState(null);
  const [bodyCategoriesSelected, setBodyCategoriesSelected] = useState(null);
  const [providersSelected, setProvidersSelected] = useState([]);
  const [platformsAffected, setPlatformsAffected] = useState([]);
  const [platformsSelected, setPlatformsSelected] = useState(null);
  const [creationConfirmed, setCreationConfirmed] = useState(false);
  const [eventsIds, setEventIds] = useState(null);
  const [maintenanceDate, setMaintenanceDate] = useState(null);
  const [maintenanceDateSelected, setMaintenanceDateSelected] = useState(null);
  const [emailBodyPreview, setEmailBodyPreview] = useState(null);
  const createFixtureFilterEntry = () => ({
    providerId: "",
    eventId: "",
    info: "",
    infoData: null,
    platforms: [],
  });

  const [fixtureSearch, setFixtureSearch] = useState([createFixtureFilterEntry()]);

  const userData = getUserData();

  const resetManualFixtures = () => {
    setFixtureSearch([createFixtureFilterEntry()]);
  };

  const resetSignalState = () => {
    setSignalTypeSelected(null);
    setBodyCategoriesSelected(null);
    setMaintenanceDate(null);
    setMaintenanceDateSelected(null);
    setProvidersSelected([]);
    setPlatformsAffected([]);
    setPlatformsSelected(null);
    setCreationConfirmed(false);
    setEmailBodyPreview(null);
    setEventIds(null);
    resetManualFixtures();
  };

  const filledFixtureRows = useMemo(
    () =>
      fixtureSearch.filter(({ providerId, eventId }) => {
        const providerTrimmed = String(providerId ?? "").trim();
        const eventTrimmed = String(eventId ?? "").trim();
        return providerTrimmed !== "" || eventTrimmed !== "";
      }),
    [fixtureSearch]
  );

  const manualFixtureEvents = useMemo(
    () =>
      filledFixtureRows
        .filter(({ providerId, eventId }) => {
          const providerTrimmed = String(providerId ?? "").trim();
          const eventTrimmed = String(eventId ?? "").trim();
          return providerTrimmed !== "" && eventTrimmed.length >= 4;
        })
        .map((entry) => ({
          providerId: String(entry.providerId ?? "").trim(),
          eventId: String(entry.eventId ?? "").trim(),
          info: entry.info,
          infoData: entry.infoData,
          platforms: entry.platforms || [],
        })),
    [filledFixtureRows]
  );

  const manualFixturesComplete = useMemo(
    () => manualFixtureEvents.length > 0 && manualFixtureEvents.length === filledFixtureRows.length,
    [manualFixtureEvents, filledFixtureRows]
  );

  const signalTypeValue = signalTypeSelected?.value;
  const isCoverageSignal = signalTypeValue === 3;
  const isBlockSignal = signalTypeValue === 4;
  const isUnblockSignal = signalTypeValue === 5;
  const isBlockFlow = isBlockSignal || isUnblockSignal;
  const hasMaintenanceSelection = maintenanceDateSelected != null && bodyCategoriesSelected != null;
  const hasCoverageEvents = Array.isArray(eventsIds)
    ? eventsIds.length > 0
    : Boolean(eventsIds && eventsIds.length > 0);
  const shouldShowActions =
    signalTypeValue === 0 ||
    signalTypeValue === 1 ||
    (signalTypeValue === 2 && hasMaintenanceSelection) ||
    (isCoverageSignal && hasCoverageEvents) ||
    isBlockFlow;
  const shouldShowCheckButton = !(isCoverageSignal || isBlockFlow);

  async function initData() {
    let getMailBodiesEndpoint = `/api/v1/entities/availableEmailTemplates/1`;
    let response = await getData(getMailBodiesEndpoint, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      setBodyCategories(
        response.data.map((item) => {
          item.value = item.id;
          item.label = item.info;
          return item;
        })
      );
    }
  }

  const handleCancelCreation = () => {
    setOpen(false);
    signalCloseModal(false);
    resetSignalState();
  };

  const handleFixtureSearchChange = (index, { target }) => {
    const { name, value } = target;

    setFixtureSearch((prev) => {
      const next = [...prev];

      if (name === "eventId") {
        const numericValue = value.replace(/\D/g, "").slice(0, 32);
        next[index] = {
          ...next[index],
          eventId: numericValue,
          info: "",
          infoData: null,
          platforms: [],
        };
      } else {
        next[index] = { ...next[index], [name]: value };
      }

      return next;
    });
  };

  const handleFixtureSearchAdd = () => {
    setFixtureSearch((prev) => [...prev, createFixtureFilterEntry()]);
  };

  const handleFixtureSearchRemove = (index) => {
    setFixtureSearch((prev) => {
      if (prev.length === 1) {
        return [createFixtureFilterEntry()];
      }

      const next = prev.filter((_, filterIndex) => filterIndex !== index);

      return next.length > 0 ? next : [createFixtureFilterEntry()];
    });
  };

  const handleFixtureProviderChange = (index, option) => {
    setFixtureSearch((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        providerId: option ? String(option.value) : "",
        info: "",
        infoData: null,
        platforms: [],
      };
      return next;
    });
  };

  const handleCreationSignal = (e) => {
    e.preventDefault();

    Swal.fire({
      title: `Confirmation`,
      html: `You are going to send the signal </br><b>"${
        signalTypeSelected?.label ?? ""
      }"</b></br> </br> Are you sure?`,
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: "Yes, Procceed!",
      denyButtonText: "Cancel",
      icon: "warning",
      confirmButtonColor: "#87cb16",
      denyButtonColor: "#ff4a55",
    }).then(async (result) => {
      if (result.isConfirmed) {
        handleCreationSignalConfirmed(e);
      }
    });
  };

  const handleCreationSignalConfirmed = async (e) => {
    e.preventDefault();

    let signalResponseData = null;
    let signalErrorResponseData = null;
    const currentSignalType = signalTypeSelected;
    const currentSignalTypeValue = currentSignalType?.value;

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let createSignalEndpoint = "";
        let signalData = { providerIds: providersSelected, platformIds: platformsSelected };

        switch (currentSignalTypeValue) {
          case 0:
            createSignalEndpoint = `/api/v1/signals/providerDown`;
            break;
          case 1:
            createSignalEndpoint = `/api/v1/signals/providerUp`;
            break;
          case 2:
            createSignalEndpoint = `/api/v1/signals/scheduledMaintenance`;
            signalData.maintenanceDate = maintenanceDateSelected;
            signalData.bodyTemplateId = bodyCategoriesSelected.value;
            break;
          case 3:
            createSignalEndpoint = `/api/v1/signals/coverageLost`;
            signalData.events = eventsIds;
            break;
          case 4:
          case 5:
            createSignalEndpoint = `/api/v2/panel/filters/fixtures/blocked`;
            signalData = manualFixtureEvents.map(({ providerId, eventId }) => ({
              fixture_id: eventId,
              provider_id: providerId,
              status: currentSignalTypeValue === 4,
            }));
            break;
          default:
            break;
        }
        if (createSignalEndpoint == "") {
          Swal.clickDeny();
          return;
        }
        if (
          (currentSignalTypeValue === 4 || currentSignalTypeValue === 5) &&
          manualFixtureEvents.length === 0
        ) {
          Swal.clickDeny();
          return;
        }
        let response = await postData(
          createSignalEndpoint,
          signalData,
          {
            Authorization: getAuthorizationHeader(),
          },
          (error) => {
            signalErrorResponseData = errorHandler(error);
          }
        );
        if (response && response.status === 200) {
          signalResponseData = response.data;
          Swal.clickConfirm();
        } else {
          signalResponseData = signalErrorResponseData;
          Swal.clickDeny();
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      setOpen(false);
      signalCloseModal(true);
      resetSignalState();
      if (result.isConfirmed) {
        //console.log(result.data);
        let message = `Signal sent to selected platforms`;
        if (currentSignalType?.value === 3) {
          message = `Signal sent`;
          let extraData = signalResponseData.map((item, rowCount) => {
            return (
              '<tr key="row-' +
              rowCount +
              '">' +
              Object.keys(item)
                .map((column, columnCount) => {
                  if (column == "available") {
                    return item[column]
                      ? '<td style="color: green" key="' +
                          rowCount +
                          "-column-" +
                          columnCount +
                          '">Coverage lost sent</td>'
                      : '<td style="color: red" key="' +
                          rowCount +
                          "-column-" +
                          columnCount +
                          '"> Event Not Found </td>';
                  } else {
                    return (
                      '<td key="' +
                      rowCount +
                      "-column-" +
                      columnCount +
                      '">' +
                      item[column] +
                      "</td>"
                    );
                  }
                })
                .join("") +
              "</tr>"
            );
          });
          message =
            message +
            " <br> <table class='coverage-lost-results' style='width:100%'><thead><tr><th>given_id</th><th>uni_id</th><th>event_id</th><th>sport</th><th>available</th></tr></thead><tbody>" +
            extraData.join("") +
            "<tbody></table>";
        }
        if (currentSignalTypeValue === 4) {
          signalResponseData.response && signalResponseData.response.statusText
            ? signalResponseData.response.statusText
            : `Signal sent`;
          let extraData = signalResponseData.response.data.map((item, rowCount) => {
            return (
              '<tr key="row-' +
              rowCount +
              '">' +
              Object.keys(item)
                .map((column, columnCount) => {
                  if (column == "available") {
                    return item[column]
                      ? '<td style="color: green" key="' +
                          rowCount +
                          "-column-" +
                          columnCount +
                          '">Coverage lost sent</td>'
                      : '<td style="color: red" key="' +
                          rowCount +
                          "-column-" +
                          columnCount +
                          '"> Event Not Found </td>';
                  } else {
                    return (
                      '<td key="' +
                      rowCount +
                      "-column-" +
                      columnCount +
                      '">' +
                      item[column] +
                      "</td>"
                    );
                  }
                })
                .join("") +
              "</tr>"
            );
          });
          message =
            message +
            " <br> <table class='coverage-lost-results' style='width:100%'><thead><tr><th>given_id</th><th>uni_id</th><th>event_id</th><th>sport</th><th>available</th></tr></thead><tbody>" +
            extraData.join("") +
            "<tbody></table>";

          Swal.fire({
            title: "Request Sent!",
            html: message,
            showDenyButton: false,
            showConfirmButton: true,
          });
          return;
        }
        Swal.fire({
          title: "Request Sent!",
          html: message,
          showDenyButton: false,
          showConfirmButton: true,
        });
      }
      if (result.isDenied) {
        let message = `Signal didn't send to the selected platforms. Please try again later!`;
        if (currentSignalTypeValue === 3) message = `Signal didn't send. Please try again later!`;
        if (currentSignalTypeValue === 4) {
          message =
            signalResponseData.response && signalResponseData.response.statusText
              ? signalResponseData.response.statusText
              : `Signal sent`;
          let extraData = signalResponseData.response.data.map((item, rowCount) => {
            return (
              '<tr key="row-' +
              rowCount +
              '">' +
              Object.keys(item)
                .map((column, columnCount) => {
                  if (column == "available") {
                    return item[column]
                      ? '<td style="color: green" key="' +
                          rowCount +
                          "-column-" +
                          columnCount +
                          '">Coverage lost sent</td>'
                      : '<td style="color: red" key="' +
                          rowCount +
                          "-column-" +
                          columnCount +
                          '"> Event Not Found </td>';
                  } else {
                    return (
                      '<td key="' +
                      rowCount +
                      "-column-" +
                      columnCount +
                      '">' +
                      item[column] +
                      "</td>"
                    );
                  }
                })
                .join("") +
              "</tr>"
            );
          });
          message =
            message +
            " <br> <table class='coverage-lost-results' style='width:100%'><thead><tr><th>given_id</th><th>uni_id</th><th>event_id</th><th>sport</th><th>available</th></tr></thead><tbody>" +
            extraData.join("") +
            "<tbody></table>";

          Swal.fire({
            title: "Error!",
            html: message,
            showDenyButton: false,
            showConfirmButton: true,
          });
          return;
        }
        Swal.fire("Error!", message, "error");
      }
    });
  };

  const errorHandler = (error) => {
    //console.log(data);
    // Swal.clickDeny();
    return error;
  };

  const handleCheck = async (e) => {
    e.preventDefault();

    let checkSignalEndpoint = `/api/v1/signals/checkSignalAffect`;
    let response = await postData(
      checkSignalEndpoint,
      { providerIds: providersSelected },
      {
        Authorization: getAuthorizationHeader(),
      }
    );
    if (response) {
      //console.log(response.data);
      setPlatformsAffected(response.data);
      setPlatformsSelected(response.data.map((item) => item.id));
    }
  };

  const getFixtureData = async (e, payload, manualIndex = null) => {
    let key = "";

    if (manualIndex !== null) {
      const entry = fixtureSearch[manualIndex];
      if (!entry || !entry.providerId || !entry.eventId) {
        return;
      }
      key = `snapshot::${entry.providerId}::*::${entry.eventId}`;
    } else if (typeof payload === "string") {
      key = payload;
    } else if (payload && typeof payload === "object") {
      if (payload.inspect) {
        key = payload.inspect;
      } else if (payload.providerId && payload.eventId) {
        key = `snapshot::${payload.providerId}::*::${payload.eventId}`;
      }
    }

    if (!key) {
      return;
    }

    let response = null;

    try {
      const checkSignalEndpoint = `/api/v1/keys/fixtures/${key}/data`;
      response = await getData(checkSignalEndpoint, {
        Authorization: getAuthorizationHeader(),
      });
    } catch (error) {
      return;
    }

    if (!response || !response.data) {
      return;
    }

    const buildInfoNode = (data) => {
      if (data && data.region) {
        return (
          <div style={{ display: "flex" }}>
            <div>{data.region.providerName} &gt; &nbsp;</div>
            <div> {data.competition.providerName} &gt; &nbsp;</div>
            <div> {data.participants[0].providerName} Vs &nbsp;</div>
            <div> {data.participants[1].providerName} </div>
          </div>
        );
      }

      return (
        <div style={{ display: "flex" }}>
          <div>Fixture not found in Snapshoter</div>
        </div>
      );
    };

    const hasRegion = Boolean(response.data?.region);

    if (manualIndex !== null) {
      setFixtureSearch((prev) => {
        if (!prev[manualIndex]) {
          return prev;
        }

        const next = [...prev];
        next[manualIndex] = {
          ...next[manualIndex],
          info: buildInfoNode(response.data),
          infoData: hasRegion ? response.data : null,
          platforms: Array.isArray(response.data?.platforms) ? response.data.platforms : [],
        };

        return next;
      });
      return;
    }
  };

  const copyToClipboard = (e, textToCopy) => {
    // setApiresponse("success");
    // setApiResponseMsg(
    //   type == "main"
    //     ? "API-KEY has been copied to the clipboard!"
    //     : "STATS-API-KEY has been copied to the clipboard!"
    // );
    // setNotification(true);
    // navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
      // navigator clipboard api method'
      return navigator.clipboard.writeText(textToCopy);
    } else {
      // text area method
      let textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      // make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
        // here the magic happens
        document.execCommand("copy") ? res() : rej();
        textArea.remove();
      });
    }
  };

  const handleSignalTypeChange = (item) => {
    setSignalTypeSelected(item);
    setMaintenanceDateSelected(null);
    setMaintenanceDate(null);
    setBodyCategoriesSelected(null);
    setEmailBodyPreview(null);
    setPlatformsAffected([]);
    setPlatformsSelected(null);
    setEventIds(null);
    resetManualFixtures();
  };

  const handleProviderChange = (event) => {
    event.preventDefault();
    //console.log("handleProviderChange:", event.target.name);
    if (!event.target.name) return;
    let providers = [...providersSelected];
    let providerIndex = providers.findIndex((provider) => provider == event.target.name);
    if (providerIndex > -1) {
      providers.splice(providerIndex, 1);
    } else {
      providers.push(event.target.name);
    }

    setProvidersSelected(providers);
    setPlatformsAffected([]);
    setPlatformsSelected(null);
  };

  const diselectPlatforms = (event) => {
    setPlatformsSelected([]);
  };

  const selectAllPlatforms = (event) => {
    if (platformsAffected.length == 0) return;
    setPlatformsSelected(userData.platforms.map((item) => item.id));
  };

  const handlePlatformChange = (event) => {
    event.preventDefault();
    if (!event.target.name) return;
    let platforms = [...platformsSelected];
    let platformIndex = platforms.findIndex((platform) => platform == event.target.name);
    if (platformIndex > -1) {
      platforms.splice(platformIndex, 1);
    } else {
      platforms.push(event.target.name);
    }

    setPlatformsSelected(platforms);
  };

  const handleBodyCategoriesChange = (item) => {
    setBodyCategoriesSelected(item);
    setEmailBodyPreview(item.html);
  };

  const handleMaintenanceDate = (item) => {
    setMaintenanceDateSelected(item[0].toUTCString());
    item.value = item[0];
    setMaintenanceDate(item);
  };

  const handleEventIdsChange = (event) => {
    event.preventDefault();

    let events = event.target.value.split(",");
    let eventsToSent = [];
    for (let index = 0; index < events.length; index++) {
      const ev = events[index].trim();
      if (parseInt(ev)) eventsToSent.push(ev);
    }

    eventsToSent = [...new Set(eventsToSent)];
    //console.log(eventsToSent);

    setEventIds(eventsToSent.length > 0 ? eventsToSent : null);
    setCreationConfirmed(eventsToSent.length > 0 ? true : false);
  };

  const selectAllProviders = (event) => {
    event.preventDefault();

    setProvidersSelected(providers.map((p) => p.value));
    setPlatformsAffected([]);
    setPlatformsSelected(null);
  };

  const diselectAllProviders = (event) => {
    event.preventDefault();

    setProvidersSelected([]);
    setPlatformsAffected([]);
    setPlatformsSelected(null);
  };

  useEffect(() => {
    setOpen(showModal);
    if (showModal) initData();
  }, [showModal]);

  useEffect(() => {
    if (sports) {
      setPlatformsAffected(userData.platforms);
      setPlatformsSelected(userData.platforms.map((item) => item.id));
    }
  }, [sports]);

  useEffect(() => {
    if (!signalTypeValue && signalTypeValue !== 0) {
      setCreationConfirmed(false);
      return;
    }

    switch (signalTypeValue) {
      case 3: {
        setCreationConfirmed(hasCoverageEvents);
        break;
      }
      case 4:
      case 5: {
        setCreationConfirmed(manualFixturesComplete);
        break;
      }
      default: {
        const hasPlatforms = Array.isArray(platformsSelected) && platformsSelected.length > 0;
        setCreationConfirmed(hasPlatforms);
        break;
      }
    }
  }, [signalTypeValue, hasCoverageEvents, manualFixturesComplete, platformsSelected]);

  return (
    <div>
      <Dialog open={open} onClose={handleCancelCreation} fullWidth={true} maxWidth={"xl"}>
        <DialogTitle>Create New Signal</DialogTitle>
        <DialogContent sx={{ minHeight: "620px" }}>
          <Grid container py={0} px={0} sx={{}}>
            <Grid item xs={5} md={5} style={{}}>
              <SoftBox py={1} px={1}>
                <SoftTypography variant="caption">Select Signal Type: </SoftTypography>
                <SoftSelect
                  id="signal-type"
                  size="small"
                  value={signalTypeSelected}
                  options={signalTypes}
                  onChange={handleSignalTypeChange}
                />
              </SoftBox>
            </Grid>
            <Grid item xs={4} md={4} style={{ display: isCoverageSignal ? "" : "none" }}>
              <SoftBox py={1} px={1}>
                <SoftTypography variant="caption">Set Event Ids: </SoftTypography>
                <HtmlTooltip
                  placement={"right"}
                  maxwidth={200}
                  title={
                    <React.Fragment>
                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                        <div style={{ display: "block" }}>
                          Event Ids (eventId, fixtureId) comma separeated
                        </div>
                      </div>
                    </React.Fragment>
                  }
                >
                  <Icon className="info-icon">info_outlined</Icon>
                </HtmlTooltip>
                <SoftInput id="event-ids" size="small" onChange={handleEventIdsChange} />
              </SoftBox>
            </Grid>
            <Grid item xs={4} md={4} style={{ display: signalTypeValue === 2 ? "" : "none" }}>
              <SoftBox py={1} px={1}>
                <SoftTypography variant="caption">Select Body Template: </SoftTypography>
                <HtmlTooltip
                  placement={"right"}
                  maxwidth={200}
                  title={
                    <React.Fragment>
                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                        <div style={{ display: "block" }}>{emailBodyPreview}</div>
                      </div>
                    </React.Fragment>
                  }
                >
                  <Icon className="info-icon">info_outlined</Icon>
                </HtmlTooltip>
                <SoftSelect
                  id="body-categories"
                  size="small"
                  value={bodyCategoriesSelected}
                  options={bodyCategories}
                  onChange={handleBodyCategoriesChange}
                />
              </SoftBox>
            </Grid>
            <Grid item xs={3} md={3} style={{ display: signalTypeValue === 2 ? "" : "none" }}>
              <SoftBox py={1} px={1}>
                <SoftTypography variant="caption">Select Maintenance Date: </SoftTypography>
                <SoftDatePicker
                  value={maintenanceDate}
                  size="small"
                  enableTime={true}
                  monthSelectorType="static"
                  onChange={(item) => handleMaintenanceDate(item)}
                />
              </SoftBox>
            </Grid>
          </Grid>

          <Grid container py={2} px={2} sx={{ display: shouldShowActions ? "" : "none" }}>
            <Grid
              sx={{
                display: isBlockFlow ? "" : "none",
              }}
              item
              xs={isBlockFlow ? 8 : 6}
              md={isBlockFlow ? 8 : 6}
              style={{ backgroundColor: "#f9f9f9" }}
            >
              <SoftBox py={2} px={2}>
                <SoftBox
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  <Grid
                    container
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                    }}
                  >
                    <Grid item xs={11} md={11}>
                      <SoftTypography variant="button" fontWeight="medium">
                        {`Find Fixtures to ${isUnblockSignal ? "Unblock" : "Block"}: `}
                      </SoftTypography>
                      <HtmlTooltip
                        placement={"right"}
                        maxwidth={240}
                        title={
                          <React.Fragment>
                            <div style={{ display: "flex", flexWrap: "wrap" }}>
                              <div style={{ display: "block" }}>
                                Use the fields to narrow the fixture list. Add provider/event pairs
                                with the plus icon, remove rows with the minus icon, or reload
                                fixtures with the refresh icon.
                              </div>
                            </div>
                          </React.Fragment>
                        }
                      >
                        <Icon className="info-icon">info_outlined</Icon>
                      </HtmlTooltip>
                    </Grid>
                    <Grid item xs={1} md={1}>
                      <Icon
                        className="info-icon-button"
                        color="primary"
                        style={{ fontSize: "20px !important", cursor: "pointer" }}
                        onClick={handleFixtureSearchAdd}
                      >
                        add
                      </Icon>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ paddingTop: "0px", height: "100%" }}
                    style={{ display: "flex", flexWrap: "wrap" }}
                  >
                    <SoftBox display="flex" flexDirection="column" gap={2} flexGrow={1}>
                      <SoftBox display="flex" flexWrap="wrap" alignItems="flex-end" gap={2}>
                        <SoftBox display="flex" flexDirection="column" minWidth="210px">
                          <SoftTypography variant="caption" color="text" mb={0.5}>
                            provider id
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox display="flex" flexDirection="column" minWidth="210px">
                          <SoftTypography variant="caption" color="text" mb={0.5}>
                            event id
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>

                      {fixtureSearch.map((entry, index) => (
                        <SoftBox
                          key={`fixture-filter-${index}`}
                          display="flex"
                          flexWrap="wrap"
                          alignItems="flex-end"
                          gap={2}
                        >
                          <SoftBox
                            display="flex"
                            flexDirection="column"
                            maxWidth="205px"
                            minWidth="160px"
                          >
                            <SoftSelect
                              id={`fixture-provider-${index}`}
                              sx={{ maxWidth: "205px" }}
                              options={providers || []}
                              value={
                                providers
                                  ? providers.find(
                                      (provider) =>
                                        String(provider.value) === String(entry.providerId)
                                    ) || null
                                  : null
                              }
                              isClearable
                              onChange={(option) => handleFixtureProviderChange(index, option)}
                              placeholder="Select provider"
                              size="small"
                            />
                          </SoftBox>
                          <SoftBox display="flex" flexDirection="column" minWidth="160px">
                            <SoftInput
                              name="eventId"
                              type="tel"
                              size="small"
                              value={entry.eventId}
                              onChange={(event) => handleFixtureSearchChange(index, event)}
                              placeholder="Type event id"
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                minLength: 4,
                                maxLength: 32,
                              }}
                            />
                          </SoftBox>
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Grid container py={0} px={0} sx={{}}>
                              <Grid item xs={6} md={6} style={{}}>
                                <SoftBox>
                                  <SoftTypography
                                    variant="body1"
                                    color="secondary"
                                    sx={{ cursor: "pointer", lineHeight: 0 }}
                                  >
                                    <HtmlTooltip
                                      placement={"right"}
                                      maxwidth="100%"
                                      title={
                                        <React.Fragment>
                                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                                            <div style={{ display: "block" }}>
                                              {entry.info && entry.info != ""
                                                ? entry.info
                                                : `Fixtures's info`}
                                            </div>
                                          </div>
                                        </React.Fragment>
                                      }
                                    >
                                      <Icon
                                        onClick={(e) =>
                                          entry.info && entry.info != ""
                                            ? null
                                            : getFixtureData(e, entry, index)
                                        }
                                        style={{
                                          color: entry.info
                                            ? entry.infoData
                                              ? "#17c1e8"
                                              : "#FF0000"
                                            : "",
                                        }}
                                      >
                                        info_outlined
                                      </Icon>
                                    </HtmlTooltip>
                                  </SoftTypography>
                                  <SoftTypography
                                    variant="body1"
                                    color="secondary"
                                    sx={{
                                      cursor: "pointer",
                                      lineHeight: 0,
                                      display: entry?.platforms?.length > 0 ? "" : "none",
                                    }}
                                  >
                                    <HtmlTooltip
                                      placement={"right"}
                                      maxwidth="100%"
                                      title={
                                        <React.Fragment>
                                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                                            <div style={{ display: "block" }}>
                                              {entry?.platforms?.length > 0 &&
                                              userData.platforms &&
                                              userData.platforms.length > 0
                                                ? entry?.platforms
                                                    .map((p) => {
                                                      return userData.platforms.find(
                                                        (ap) => ap.id == p
                                                      ).name;
                                                    })
                                                    .join(", ")
                                                : `no platforms`}
                                            </div>
                                          </div>
                                        </React.Fragment>
                                      }
                                    >
                                      <Icon
                                        style={{
                                          color: entry?.platforms?.length > 0 ? "#17c1e8" : "",
                                        }}
                                      >
                                        newspaper_icon
                                      </Icon>
                                    </HtmlTooltip>
                                  </SoftTypography>
                                </SoftBox>
                              </Grid>
                              <Grid>
                                <SoftBox>
                                  <SoftTypography
                                    variant="body1"
                                    color="secondary"
                                    sx={{
                                      display: entry.infoData ? "" : "none",
                                      cursor: "pointer",
                                      lineHeight: 0,
                                    }}
                                  >
                                    <HtmlTooltip
                                      placement={"right"}
                                      maxwidth="100%"
                                      title={
                                        <React.Fragment>
                                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                                            <div style={{ display: "block" }}>
                                              {entry.info && entry.info !== ""
                                                ? entry.info
                                                : `Fixtures's info`}
                                            </div>
                                          </div>
                                        </React.Fragment>
                                      }
                                    >
                                      <Icon
                                        onClick={(e) =>
                                          entry.infoData
                                            ? copyToClipboard(
                                                e,
                                                `${entry.infoData.region.providerName} > ${entry.infoData.competition.providerName} > ${entry.infoData.participants[0].providerName} Vs ${entry.infoData.participants[1].providerName}`
                                              )
                                            : null
                                        }
                                        style={{
                                          color: entry.infoData ? "#17c1e8" : "",
                                        }}
                                      >
                                        copy_outlined
                                      </Icon>
                                    </HtmlTooltip>
                                  </SoftTypography>
                                </SoftBox>
                              </Grid>
                            </Grid>
                            <Grid container py={0} px={0} sx={{}}>
                              <Grid item xs={12} md={12} style={{ paddingLeft: "20px" }}>
                                <SoftTypography
                                  variant="body1"
                                  color="secondary"
                                  sx={{ cursor: "pointer", lineHeight: 0 }}
                                >
                                  <Icon
                                    className={
                                      fixtureSearch.length === 1 && index === 0
                                        ? ""
                                        : "error-icon-button"
                                    }
                                    onClick={() => handleFixtureSearchRemove(index)}
                                    disabled={fixtureSearch.length === 1 && index === 0}
                                  >
                                    highlight_off_icon
                                  </Icon>
                                </SoftTypography>
                              </Grid>
                            </Grid>
                          </SoftBox>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  </Grid>
                </SoftBox>
              </SoftBox>
            </Grid>
            <Grid
              sx={{ display: isBlockFlow ? "none" : "" }}
              item
              xs={6}
              md={6}
              style={{ backgroundColor: "#f9f9f9" }}
            >
              <SoftBox py={2} px={2}>
                <FormControl sx={{ width: "100%" }}>
                  <SoftBox pb={2} sx={{ display: "flex" }}>
                    <Grid item xs={6} md={6}>
                      <SoftTypography variant="h6" fontWeight="medium">
                        Available Providers:
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={6} md={6} sx={{ display: "flex" }}>
                      <Grid item xs={6} md={6}>
                        <SoftButton
                          type="submit"
                          variant="outlined"
                          color="info"
                          size="small"
                          className="select-diselect"
                          onClick={selectAllProviders}
                        >
                          all
                        </SoftButton>
                      </Grid>
                      <Grid item xs={6} md={6}>
                        <SoftButton
                          type="submit"
                          variant="outlined"
                          color="info"
                          size="small"
                          className="select-diselect"
                          onClick={diselectAllProviders}
                        >
                          none
                        </SoftButton>
                      </Grid>
                    </Grid>
                  </SoftBox>
                  <FormGroup row className="signals-available-providers">
                    {providers
                      ? providers.map((provider) => (
                          <FormGroup
                            row
                            key={`${provider.value}-holder`}
                            className="available-providers-item"
                          >
                            <FormControlLabel
                              className="providers-checkbox"
                              control={
                                <Checkbox
                                  name={String(provider.value)}
                                  checked={
                                    providersSelected
                                      ? providersSelected.find((item) => item == provider.value)
                                        ? true
                                        : false
                                      : false
                                  }
                                  onClick={handleProviderChange}
                                />
                              }
                              key={`${provider.value}-checkbox`}
                            />
                            <SoftTypography
                              key={`${provider.value}-label`}
                              className="provider-checkbox-label"
                              sx={{ minWidth: "40px !important" }}
                            >
                              {provider.label}
                            </SoftTypography>
                          </FormGroup>
                        ))
                      : ""}
                  </FormGroup>
                </FormControl>
              </SoftBox>
            </Grid>
            <Grid
              item
              xs={isBlockFlow ? 4 : 6}
              md={isBlockFlow ? 4 : 6}
              sx={{ display: isBlockFlow ? "none" : "" }}
            >
              <SoftBox py={2} px={2}>
                <FormControl>
                  <SoftBox pb={2}>
                    <SoftTypography variant="h6" fontWeight="medium">
                      Affected Platforms:
                    </SoftTypography>
                  </SoftBox>
                  <Grid>
                    <Button onClick={selectAllPlatforms}>Select all</Button>
                    <Button onClick={diselectPlatforms}>Diselect all</Button>
                  </Grid>
                  <FormGroup
                    row
                    className="signals-available-providers"
                    style={{ maxHeight: "fit-content" }}
                  >
                    {platformsAffected
                      ? platformsAffected.map((platform) => (
                          <FormGroup
                            row
                            key={`${platform.id}-holder`}
                            className="signal-available-providers-item"
                          >
                            <FormControlLabel
                              className="providers-checkbox"
                              sx={{ lineHeight: "1 !important" }}
                              control={
                                <Checkbox
                                  name={String(platform.id)}
                                  checked={
                                    platformsSelected
                                      ? platformsSelected.find((item) => item == platform.id)
                                        ? true
                                        : false
                                      : false
                                  }
                                  onClick={handlePlatformChange}
                                />
                              }
                              key={`${platform.id}-checkbox`}
                            />
                            <Grid sx={{ marginBottom: "5px" }}>
                              <Grid item md={12}>
                                <SoftTypography
                                  key={`${platform.id}-label`}
                                  className="provider-checkbox-label"
                                  sx={{ minWidth: "40px !important", lineHeight: 1 }}
                                >
                                  {platform.name}
                                </SoftTypography>
                              </Grid>
                              <Grid item md={12}>
                                <SoftTypography
                                  key={`${platform.name}-label`}
                                  sx={{
                                    fontSize: ".70rem",
                                    color: "#a4a5ac",
                                    lineHeight: 1,
                                    maxWidth: "270px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {platform.pids
                                    ? "(" +
                                      platform.pids
                                        .map((provider) => {
                                          return providers.find((p) => p.value == provider)[
                                            "label"
                                          ];
                                        })
                                        .join(", ") +
                                      ")"
                                    : ""}
                                </SoftTypography>
                              </Grid>
                            </Grid>
                          </FormGroup>
                        ))
                      : ""}
                  </FormGroup>
                </FormControl>
              </SoftBox>
            </Grid>
          </Grid>
          <Grid container py={2} px={2} sx={{ display: shouldShowActions ? "" : "none" }}>
            <Grid item xs={12} md={12} style={{}}>
              <SoftBox>
                <Grid container justifyContent="right">
                  <Grid item py={1} px={1}>
                    <SoftButton variant="outlined" color="info" onClick={handleCancelCreation}>
                      Cancel
                    </SoftButton>
                  </Grid>
                  <Grid item py={1} px={1} sx={{ display: shouldShowCheckButton ? "" : "none" }}>
                    <SoftButton
                      type="submit"
                      variant="contained"
                      disabled={providersSelected && providersSelected.length > 0 ? false : true}
                      color="warning"
                      onClick={(e) => handleCheck(e)}
                    >
                      Check for Affected Platforms
                    </SoftButton>
                  </Grid>
                  <Grid item py={1} px={1}>
                    <SoftButton
                      type="submit"
                      variant="gradient"
                      disabled={!creationConfirmed}
                      color="dark"
                      onClick={(e) => handleCreationSignal(e)}
                    >
                      Send Signal
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Setting default values for the props of CreatePlatform
CreateSignal.defaultProps = {
  showModal: true,
  signalCloseModal: () => {
    //console.log("close Modal");
  },
};

// Typechecking props of the CreatePlatform
CreateSignal.propTypes = {
  showModal: PropTypes.bool,
  signalCloseModal: PropTypes.func,
};

export default CreateSignal;
